import { useCallback, useMemo } from 'react';
import analyticsService from '../services/analytics/analyticsService';
import { analytics } from '../services/analytics/analytics';
import userTrackingService from '../services/users/userTrackingService';
import { createFileLogger } from '../utils/logger';

export const useAnalytics = () => {
  const log = useMemo(() => createFileLogger('ANALYTICS_HOOK'), []);

  // Track successful login
  const trackLogin = useCallback((method: string, userId: string, userInfo?: Record<string, any>) => {
    userTrackingService.setUser(userId);
    if (userInfo) {
      userTrackingService.updateUserInfo(userInfo);
    }

    analyticsService.trackLogin(method, userId);

    analyticsService.setUserProperties({
      user_id: userId,
      properties: {
        ...(userInfo?.email && userInfo.email.trim() !== '' && { $email: userInfo.email }),
        ...(userInfo?.name && userInfo.name.trim() !== '' && { $name: userInfo.name }),
        login_method: method,
        last_login: new Date().toISOString(),
        total_logins: (userInfo?.total_logins || 0) + 1,
        user_type: 'authenticated',
        ...userInfo,
      },
    });

    log.debug('[LOGIN] User logged in and tracked:', { userId, method });
  }, [log]);

  // Track app opens (canonical: app_opened)
  const trackAppOpen = useCallback((appName: string, context?: Record<string, any>) => {
    analytics.track('app_opened', {
      app_name: appName,
      source: context?.source,
      ...context,
    });
  }, []);

  // Track document clicks (canonical: document_clicked)
  const trackDocClick = useCallback((docId: string, _docName: string, context?: Record<string, any>) => {
    analytics.track('document_clicked', {
      doc_id: docId,
      source: context?.source,
      ...context,
    });
  }, []);

  // Track document shares (canonical: document_shared)
  const trackDocShare = useCallback((docId: string, shareMethod: string, context?: Record<string, any>) => {
    analytics.track('document_shared', {
      doc_id: docId,
      share_type: shareMethod,
      ...context,
    });
  }, []);

  // Track error pages (canonical: error_page_viewed)
  const trackErrorPage = useCallback((errorType: string, errorMessage?: string, context?: Record<string, any>) => {
    analytics.track('error_page_viewed', {
      error_code: errorType,
      path: window.location.pathname,
      ...context,
    });
    log.debug('[ERROR_PAGE] Tracked error page:', { errorType, errorMessage });
  }, [log]);

  return {
    trackLogin,
    trackAppOpen,
    trackDocClick,
    trackDocShare,
    trackErrorPage,
    track: analyticsService.track.bind(analyticsService),
    trackPageView: analyticsService.trackPageView.bind(analyticsService),
    setUserProperties: analyticsService.setUserProperties.bind(analyticsService),
  };
};

export default useAnalytics;
